<template>
  <div class="flex flex-col">
    <div class="flex flex-col justify-end">
      <!-- Intro -->
      <div class="flex flex-col gap-[32px] mb-[32px]">
        <div class="flex flex-row items-center gap-[8px]">
          <div
              class="flex justify-center items-center h-[40px] w-[40px] bg-complementary-600 text-complementary-900 rounded-full"
          >
            <span class="material-symbols-rounded">add_card</span>
          </div>
          <div class="flex flex-col">
            <h1 class="text-primary-700 text-xl md:text-3xl font-bold">
              הוספת כרטיס אשראי חדש
            </h1>
            <p class="text-purple_semantic text-base font-regular">
              פרטי תשלום
            </p>
          </div>
        </div>
        <error-block v-if="requestError" :error-message="errorMessage"/>
      </div>
      <!-- Content -->
      <div class="flex flex-row-reverse justify-end items-center gap-[50px]">
        <!-- Card icon -->
        <div class="hidden lg:flex lg:h-[233px] lg:w-[375px] shadow-popup">
          <img
              alt="card"
              class="w-full h-full object-contain"
              src="/src/assets/images/card.jpg"
          />
        </div>
        <div class="flex flex-col gal-[36px]">
          <!-- Form -->
          <div class="flex flex-col gap-[14px] text-left">
            <input-default
                v-maska
                :error="v$.cardNumber.$error"
                :errorMessage="v$.cardNumber.$errors[0]?.$message"
                :label="'מספר אשראי'"
                :model-value="cardNumber"
                autocomplete="cc-number"
                class="text-dir-ltr"
                data-maska="#### #### #### ####"
                @update:model-value="setCardNumber"
            >
              <template v-slot:left_icon>
                <span
                    class="material-symbols-rounded text-grays-700 text-[20px]"
                >credit_card</span
                >
              </template>
            </input-default>
            <div class="flex flex-row-reverse justify-end gap-[20px]">
              <div class="flex w-[100px]">
                <input-default
                    v-maska
                    :error="v$.cvv.$error"
                    :errorMessage="v$.cvv.$errors[0]?.$message"
                    :label="'CVV'"
                    :model-value="cvv"
                    :placeholder="''"
                    :widthAuto="true"
                    autocomplete="cc-csc"
                    data-maska="####"
                    @update:model-value="setCvv"
                >
                  <template v-slot:label_icon>
                    <div class="relative group">
                      <span
                          class="material-symbols-rounded text-grays-700 text-[16px]"
                      >error</span
                      >
                      <tooltip-cvv class="opacity-0 group-hover:opacity-100"/>
                    </div>
                  </template>
                </input-default>
              </div>
              <div class="flex w-[180px]">
                <input-default
                    v-maska
                    :error="v$.expirationDate.$error"
                    :errorMessage="v$.expirationDate.$errors[0]?.$message"
                    :label="'תוקף'"
                    :model-value="expirationDate"
                    :placeholder="''"
                    :widthAuto="true"
                    data-maska="##/##"
                    @update:model-value="setExpirationDate"
                >
                </input-default>
              </div>
            </div>
            <input-default
                :error="v$.idNumber.$error"
                :errorMessage="v$.idNumber.$errors[0]?.$message"
                :label="'תעודת זהות בעל הכרטיס'"
                :model-value="idNumber"
                @update:model-value="setIdNumber"
            />
          </div>
        </div>
      </div>
    </div>
    <!-- Current step. -->
    <p class="text-lg text-grays-900 font-regular absolute top-[100px] lg:top-[193px]">
      שלב 2 מתוך 2
    </p>
    <!-- Next Step Btn. -->
    <div
        class="flex flex-row-reverse justify-end w-full py-[10px] lg:py-[38px] pr-[20px] lg:pr-[114px] pl-[20px] lg:pl-0 fixed right-0 bottom-0 items-center gap-[16px] bg-white"
    >
      <div class="flex flex-row gap-[8px]">
        <p class="text-grays-900 text-base font-regular">לחזרה להרשמה מהירה</p>
        <button
            class="text-primary-700 text-base font-bold underline hover:text-primary-800 focus:text-primary-900 active:text-primary-900 disabled:text-grays-600"
            @click="handleCompleteRegistrationLater"
        >
          לחזרה להרשמה מהירה
        </button>
      </div>
      <button-primary @click="handleFinishRegistration">
        {{ isLoading ? "טוען נתונים..." : "סיימנו!" }}
      </button-primary>
    </div>
  </div>
</template>

<script>
import useValidate from "@vuelidate/core";
import {vMaska} from "maska";
import {cardValidation} from "@/validation/Card";
import {mapGetters, mapMutations} from "vuex";
import axiosInstance from "../../helpers/axios.js";
import ErrorBlock from "@/components/Global/ErrorBlock.vue";
import InputDefault from "@/components/Global/InputDefault.vue";
import TooltipCvv from "@/components/Global/TooltipCVV.vue";
import ButtonPrimary from "@/components/Global/ButtonPrimary.vue";

export default {
  name: "complete-card",
  components: {ButtonPrimary, TooltipCvv, InputDefault, ErrorBlock},
  directives: {maska: vMaska},
  data() {
    return {
      v$: useValidate(),
      isLoading: false,
      requestError: false,
      errorMessage: ''
    };
  },
  validations() {
    return {
      cardNumber: cardValidation.cardNumber,
      cvv: cardValidation.cvv,
      expirationDate: cardValidation.expirationDate,
      idNumber: cardValidation.idNumber,
    };
  },
  methods: {
    ...mapMutations({
      setCardNumber: "card/setCardNumber",
      setCvv: "card/setCvv",
      setExpirationDate: "card/setExpirationDate",
      setIdNumber: "card/setIdNumber",
    }),
    async handleFinishRegistration() {
      this.v$.$validate();
      if (!this.v$.$error) {
        this.isLoading = true;
        try {
          // Form data we collect.
          const cardNumberSanitized = this.cardNumber.replace(/\D/g, "");
          const expirationDateSanitized = this.expirationDate.replace(
              /\D/g,
              ""
          );

          const dataForm = {
            save_card: cardNumberSanitized,
            save_tokef: expirationDateSanitized,
            save_taz: this.idNumber,
          };
          // Post credit card request.
          localStorage.setItem("token", this.token);
          await axiosInstance.post("/credit_card", dataForm);
          localStorage.removeItem("token");
          // Hiding the request error in case it was true before.
          this.requestError = false;
          // Redirecting user to Email Verification page.
          this.$router.push("/send-email-verification");
        } catch (e) {
          console.error(e);
          this.errorMessage = e.response?.data?.message || '';
          // Showing requestErrorBlock in case there is any request errors.
          this.requestError = true;
        } finally {
          this.isLoading = false;
        }
      }
    },
    handleCompleteRegistrationLater() {
      this.$router.push("/send-email-verification");
    },
  },
  computed: {
    ...mapGetters({
      cardNumber: "card/getCardNumber",
      cvv: "card/getCvv",
      expirationDate: "card/getExpirationDate",
      idNumber: "card/getIdNumber",
      token: "user/getToken",
    }),
  },
};
</script>

<style>
</style>